/* eslint-disable no-console */
import { HttpContextToken, HttpEvent, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { NotificationsService } from '../../core/service/notifications.service';
import { SpinnerService } from '../../core/service/spinner.service';
import { FRONT_URLS } from "../common/url-parts";
import * as Sentry from "@sentry/angular";

export const IGNORE_ERROR_HANDLING = new HttpContextToken<boolean>(() => false);

@Injectable({ providedIn: 'root' })
export class ErrorInterceptor {
  private router = inject(Router);
  private notificationService = inject(NotificationsService);
  private spinner = inject(SpinnerService);

  intercept(
    request: HttpRequest<any>,
    next: (req: HttpRequest<any>) => Observable<HttpEvent<any>>,
  ): Observable<HttpEvent<any>> {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return request.context?.get(IGNORE_ERROR_HANDLING) ?
      next(request).pipe(catchError(this.handleError.bind(this))) :
      next(request).pipe(catchError(this.handleErrorWithNotification.bind(this)));
  }

  handleError(error: any): Observable<HttpEvent<any>> {
    this.spinner.resetActiveRequests();
    let errorMessage = '';
    if (error.error instanceof ErrorEvent) {
      // client-side error
    } else {
      // server-side error
      if (error?.error === "Captcha Unsolved") {
        return throwError("Captcha Unsolved");
      }
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
      switch (error.status) {
        case 401: { //  unauthorized
          break;
        }
        case 403: { // forbidden - user is disabled
          this.router.navigate([FRONT_URLS.BAN]);
          break;
        }
        case 451: { // 451 - Unavailable For Legal Reasons
          this.router.navigate([FRONT_URLS.TERMS_AND_CONDITIONS]);
          break;
        }
        case 502: { // Backend just started but yet initialized
          this.router.navigate([FRONT_URLS.MAINTENANCE]);
          break;
        }
        case 503: { // maintenance mode
          this.router.navigate([FRONT_URLS.MAINTENANCE]);
          break;
        }
      }
      return throwError(errorMessage);
    }
  }

  /**
   * Handles the error and tries to set the problem message in the notification service.
   * If the message containing html code with 502 from the AWS - AWS is most likely updating backend, therefore this message is ignored.
   *
   * @param error must not be null.
   */
  handleErrorWithNotification(error: any): Observable<HttpEvent<any>> {
    if (this.isAWSUpdatingBackendInstance(error?.error)) {
      return this.handleError(error);
    }

    console.log('{------------------------------');
    console.log('>>> ERROR: ', error);
    console.log('>>> ERROR STATUS: ', error.status);

    try {
      if (error.error?.error) {
        console.log('>>> ERROR MESSAGE: ', error.error.error);
        Sentry.captureException(error.error.error, {
          originalException: error,
        });
      } else if (error.error?.errors) {
        console.log('>>> ERROR MESSAGE: ', error.error.errors);
        this.notificationService.error({ title: error.error.message, message: error.error.errors });
        Sentry.captureException(error.error.errors, {
          originalException: error,
        });
      } else if (error.error?.message) {
        console.log('>>> ERROR MESSAGE: ', error.error.message);
        this.notificationService.error({ title: error.error.header, message: error.error.message });
        const sentryErrorLog = new Error(`${error.status}: '${error.error.header}': '${error.error.message}'. Place: ${error.url}`);
        Sentry.captureException(sentryErrorLog, {
          originalException: error,
        });
      } else if (error?.error === "Captcha Unsolved") {
        // notification for captcha will be executed separately
        Sentry.captureException(error.error ?? error, {
          originalException: error,
        });
        console.log('>>> ERROR MESSAGE: ', error.error ?? error);
      } else {
        this.notificationService.error({ title: 'Error', message: 'Something went wrong. Please try again later.' });
        Sentry.captureException(error.error ?? error, {
          originalException: error,
        });
        console.log('>>> ERROR MESSAGE: ', error.error ?? error);
      }
    } catch (err) {
      console.error('>>> Error while capturing the error:', err);
      Sentry.captureException(err);
    }

    console.log('------------------------------}');
    return this.handleError(error);
  }

  private isAWSUpdatingBackendInstance(error: string): boolean {
    if (!error || typeof error !== 'string') {
      return false;
    }
    return error.includes('<title>502 Bad Gateway</title>');
  }
}
